import { Fragment, ReactNode } from 'react';
import CircularProgress from '@mui/material/CircularProgress';
import { KnowledgeHubSearchResult } from 'services/types';
import {
  Highlight,
  NoResults,
  NoResultsText,
  NoResultsTitle,
  ResultItem,
  ResultSnippet,
  ResultTitle,
  ResultsDropdown,
  StatusRow,
} from './styles';

interface KnowledgeHubSearchResultsProps {
  results: KnowledgeHubSearchResult[];
  loading: boolean;
  error: string | null;
  hasSearched: boolean;
  query: string;
  onSelect: (pageId: string) => void;
}

const escapeRegExp = (value: string): string => value.replace(/[.*+?^${}()|[\]\\]/g, String.raw`\$&`);

/**
 * Wrap every occurrence of the query (whole phrase and individual words) in the
 * given text with a highlight, matched case-insensitively.
 */
function highlightMatches(text: string, query: string): ReactNode {
  const terms = Array.from(
    new Set(
      [query.trim(), ...query.trim().split(/\s+/)]
        .map((term) => term.trim())
        .filter((term) => term.length > 0),
    ),
  ).sort((a, b) => b.length - a.length);

  if (!text || terms.length === 0) {
    return text;
  }

  const termSet = new Set(terms.map((term) => term.toLowerCase()));
  const pattern = new RegExp(`(${terms.map(escapeRegExp).join('|')})`, 'gi');
  const segments = text.split(pattern);

  return segments.map((segment, index) =>
    segment && termSet.has(segment.toLowerCase()) ? (
      <Highlight key={`${segment}-${index}`}>{segment}</Highlight>
    ) : (
      <Fragment key={`${segment}-${index}`}>{segment}</Fragment>
    ),
  );
}

/**
 * Renders the Knowledge Hub search dropdown (loading, error, results and the
 * no-results state). The parent must be positioned relatively so the dropdown
 * anchors to the search input.
 */
function KnowledgeHubSearchResults({
  results,
  loading,
  error,
  hasSearched,
  query,
  onSelect,
}: Readonly<KnowledgeHubSearchResultsProps>) {
  if (loading) {
    return (
      <ResultsDropdown>
        <StatusRow>
          <CircularProgress size={16} sx={{ color: '#EB0A1E' }} />
          Searching…
        </StatusRow>
      </ResultsDropdown>
    );
  }

  if (error) {
    return (
      <ResultsDropdown>
        <StatusRow>{error}</StatusRow>
      </ResultsDropdown>
    );
  }

  if (!hasSearched) {
    return null;
  }

  if (results.length === 0) {
    return (
      <ResultsDropdown>
        <NoResults>
          <NoResultsTitle>No results found</NoResultsTitle>
          <NoResultsText>Try a different keyword or check your spelling.</NoResultsText>
        </NoResults>
      </ResultsDropdown>
    );
  }

  return (
    <ResultsDropdown>
      {results.map((result) => (
        <ResultItem
          key={result.page_id}
          type="button"
          onMouseDown={(event) => {
            // Prevent the input's blur from closing the dropdown before the click resolves.
            event.preventDefault();
            onSelect(result.page_id);
          }}
        >
          <ResultTitle>{highlightMatches(result.title, query)}</ResultTitle>
          {result.excerpt && (
            <ResultSnippet>{highlightMatches(result.excerpt, query)}</ResultSnippet>
          )}
        </ResultItem>
      ))}
    </ResultsDropdown>
  );
}

export default KnowledgeHubSearchResults;
